import classNames from 'classnames';
import { subMonths } from 'date-fns';
import React, { useEffect, useRef, useState } from 'react';
import { useAddNewCardTask } from '../../../hooks/apolloHooks/useAddNewCardTask';
import { useAddReplacementCardTask } from '../../../hooks/apolloHooks/useAddReplacementCardTask';
import { useGetCustomer } from '../../../hooks/apolloHooks/useGetCustomer';
import { GET_TRANSACTIONS_CUSTOMER_DATA_QUERY } from '../../../integrations/allkort/graphql/gql/CustomerQueries';
import { TransactionsCustomerDataResponse, UnifiedCard } from '../../../integrations/allkort/graphql/interface/Customer';
import { AddNewCardCustomerTaskInput } from '../../../integrations/allkort/graphql/interface/CustomerTaskMutation';
import { validateCardPin } from '../../../utils/cardValidationSchemas';
import Button from '../../elements/Button';
import Link from '../../elements/Link';
import Tooltip from '../../elements/Tooltip';
import { AddCardTwoIcon, CloseIcon, InfoIconOutlined, SimpleCheckmarkIcon } from '../../svgs/ExpressonSVGs';

type OrderNewCardProps = {
    orderReplacement: boolean;
    isOpen: boolean;
    isCompany: boolean;
    hasK?: boolean;
    hasP?: boolean;
    cardType?: number;
    card?: UnifiedCard;
    onClose: () => void;
    onSuccess?: () => void;
}

const OrderNewCard: React.FC<OrderNewCardProps> = ({ orderReplacement, isOpen, isCompany, hasK = false, hasP = false, card, onClose, onSuccess }) => {
    const dialogRef = useRef<HTMLDialogElement>(null);
    const pinConfirmRef = useRef<HTMLInputElement>(null);

    const [cardholderName, setCardholderName] = useState('');
    const [pin, setPin] = useState('');
    const [pinConfirm, setPinConfirm] = useState('');

    const [keepPin, setKeepPin] = useState(true);

    const [creditCheckboxChecked, setCreditCheckboxChecked] = useState(false);

    const [pinError, setPinError] = useState<string | null>(null);
    const [pinConfirmError, setPinConfirmError] = useState<string | null>(null);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [selectedCardType, setSelectedCardType] = useState<string>(hasK ? (isCompany ? 'Företagskort' : 'Tankkort') : 'Smartkort');
    const [disableNow, setDisableNow] = useState<boolean>(true);
    const [success, setSuccess] = useState(false);

    const { addNewCardTask, loading: newCardLoading, error: newCardError } = useAddNewCardTask();
    const { addReplacementCardTask, loading: replacementLoading, error: replacementError } = useAddReplacementCardTask();

    const mutationLoading = newCardLoading || replacementLoading;
    const mutationError = newCardError || replacementError;

    const isCreditCardOrder = orderReplacement
        ? card?.type.shortName === 'K'
        : selectedCardType !== 'Smartkort';
    const shouldCheckCreditCardUsage = isCreditCardOrder && (orderReplacement || hasK);

    const {
        data: creditCardTransactionData,
        loading: creditCardTransactionLoading,
        error: creditCardTransactionError,
    } = useGetCustomer<TransactionsCustomerDataResponse>(
        GET_TRANSACTIONS_CUSTOMER_DATA_QUERY,
        { last: 1, where: { transactionType: { eq: 'K' } } },
        { skip: !isOpen || !shouldCheckCreditCardUsage, fetchPolicy: 'network-only' }
    );

    const creditCardTransactions = creditCardTransactionData?.currentCustomer?.transactions?.nodes;
    const lastCreditCardTransactionAt = creditCardTransactions?.[0]?.at;
    const lastCreditCardTransactionDate = lastCreditCardTransactionAt
        ? new Date(lastCreditCardTransactionAt)
        : null;
    const needNewCreditCheck = shouldCheckCreditCardUsage
        && Boolean(creditCardTransactionData)
        && !creditCardTransactionLoading
        && !creditCardTransactionError
        && (!lastCreditCardTransactionDate
            || Number.isNaN(lastCreditCardTransactionDate.getTime())
            || lastCreditCardTransactionDate < subMonths(new Date(), 6));
    const creditCheckEvaluationError = shouldCheckCreditCardUsage && Boolean(creditCardTransactionError);
    const creditCheckEvaluationPending = shouldCheckCreditCardUsage
        && !creditCardTransactionError
        && (creditCardTransactionLoading || !creditCardTransactionData);
    const creditCheckSubmissionBlocked = shouldCheckCreditCardUsage
        && (creditCheckEvaluationPending
            || creditCheckEvaluationError
            || (needNewCreditCheck && !creditCheckboxChecked));
    const creditCheckStatusMessage = creditCheckEvaluationError
        ? 'Det gick inte att kontrollera kortets senaste transaktioner. Stäng beställningen och öppna den igen för att försöka på nytt.'
        : creditCheckEvaluationPending
            ? '' // Kontrollerar kortets senaste transaktioner...
            : null;

    useEffect(() => {
        if (isOpen) {
            dialogRef.current?.showModal();
        } else {
            dialogRef.current?.close();
        }
    }, [isOpen]);

    const handleClose = () => {
        onClose();
        // Do NOT call onSuccess here — that's only for a completed order.
        // Closing via X or Escape should not trigger a refetch.
    }

    const handleCardholderNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setCardholderName(value);
    };

    const handlePinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (value && !/^\d+$/.test(value)) return;
        setPin(value);

        if (value.length === 4) {
            const validation = validateCardPin(value);
            setPinError(validation.valid ? null : (validation.error || 'Felaktig PIN-kod'));
            if (validation.valid) {
                pinConfirmRef.current?.focus();
            }
            if (pinConfirm && pinConfirm !== value) {
                setPinConfirmError('PIN-koderna matchar inte');
            } else if (pinConfirm) {
                setPinConfirmError(null);
            }
        } else {
            setPinError(null);
        }
    };

    const handlePinConfirmChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (value && !/^\d+$/.test(value)) return;
        setPinConfirm(value);
        if (value.length === 4 && value !== pin) {
            setPinConfirmError('PIN-koderna matchar inte');
        } else {
            setPinConfirmError(null);
        }
    };

    // New card form: PIN is always required
    const isFormValid =
        pin.length === 4 && !pinError &&
        pinConfirm.length === 4 && !pinConfirmError &&
        pin === pinConfirm;

    // Replacement card form: name is optional; PIN only required when keepPin is false
    const isReplacementFormValid = keepPin
        ? true
        : pin.length === 4 && !pinError &&
        pinConfirm.length === 4 && !pinConfirmError &&
        pin === pinConfirm;

    const cardTypeId: AddNewCardCustomerTaskInput['cardTypeId'] =
        selectedCardType === 'Smartkort' ? 2 : 1;

    // Get the identifier reference
    const trimmedPan = card?.maskedPan?.replace(/[* ]/g, '')
    const identifierReference = card?.type.shortName === 'K' ?
        (isCompany ?
            'Företagskort, ' + trimmedPan
            : 'Tankkort, ' + trimmedPan)
        : 'Smartkort, ' + trimmedPan

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitError(null);
        try {
            // cardholderName is cleared by the useEffect when the dialog opens if the current card
            // has an auto-generated name, so at submit time it always contains what the user entered.
            const cardReference = cardholderName.trim();

            if (orderReplacement) {
                const today = new Date();
                const fiveMinutesFromNow = new Date(today.getTime() + 5 * 60 * 1000);
                const tenDaysFromNow = new Date(today);
                tenDaysFromNow.setDate(tenDaysFromNow.getDate() + 10);

                const result = await addReplacementCardTask({
                    cardId: parseInt(card!.id),
                    disableNow,
                    disableDueDate: disableNow ? null : tenDaysFromNow.toISOString(),
                    hasApprovedCreditCheck: creditCheckboxChecked,
                    inheritPinCode: keepPin,
                    pinCode: keepPin ? null : parseInt(pin),
                    reference1: '', // automatic filled by allkort
                    reference2: cardReference,
                    replacementDueDate: disableNow ? fiveMinutesFromNow.toISOString() : null,
                });
                //console.log('[OrderNewCard] Replacement mutation result:', result);
            } else {
                const result = await addNewCardTask({
                    cardTypeId,
                    pinCode: parseInt(pin),
                    reference1: '', // automatic filled by allkort
                    reference2: cardReference,
                });
                console.log('[OrderNewCard] New card mutation result:', result);
            }

            setSuccess(true);
        } catch (err) {
            console.error('[OrderNewCard] Mutation error:', err);
            setSubmitError('Något gick fel. Försök igen eller kontakta kundservice.');
        }
    }

    const cardUrl = isCompany ?
        'https://qstar.se/foretagskort/#kort' :
        'https://qstar.se/medlem/#kort';
    const submitButtonText = orderReplacement ? 'Beställ ersättningskort' : 'Beställ nytt kort';
    const creditCardLabel = isCompany ? 'Företagskort' : 'Tankkort';
    const cardTypeTooltipText = hasK
        ? `Du kan beställa fler ${creditCardLabel} och Smartkort här.`
        : `${creditCardLabel} kräver en separat ansökan via länken ovan.`;

    const cardType = card?.type.shortName === 'K' ? isCompany ? 'Företagskort' : 'Tankkort' : card?.type.shortName === 'P' ? 'Smartkort' : '';

    /**
     * For replacement card form
     * Fill the card holder name with the current card name as default, but allow the user to change it if they want to.
     * Only runs when the dialog is open. If the card name is an auto-generated name (contains a card type keyword
     * like "Tankkort", "Smartkort", "Företagskort", optionally followed by a card number), clear it instead of prefilling.
     */
    useEffect(() => {
        if (!isOpen || !orderReplacement) return;

        const cardName = card?.name || '';
        const generatedCardNamePattern = /(KORT|SMARTKORT|FÖRETAGSKORT|TANKKORT|SMART)/i;

        setCardholderName(generatedCardNamePattern.test(cardName) ? '' : cardName);
    }, [card, orderReplacement, isOpen]);

    useEffect(() => {
        if (isOpen) {
            setCreditCheckboxChecked(false);
        }
    }, [isOpen, orderReplacement, card?.id, selectedCardType]);



    return (
        <dialog
            ref={dialogRef}
            className={classNames("order-new-card-dialog", { 'order-replacement-dialog': orderReplacement })}
            onCancel={handleClose}
        >
            <div className="order-new-card-dialog__content">
                <h2 className="order-new-card-dialog__title">
                    {!success ? (
                        <span className="svg-icon-wrapper blue">
                            <AddCardTwoIcon width={28} height={28} />
                        </span>
                    ) : (
                        <span className="svg-icon-wrapper round green">
                            <SimpleCheckmarkIcon width={28} height={28} />
                        </span>
                    )}

                    {!success ? (
                        <>
                            Beställ {orderReplacement ? 'ersättningskort' : 'nytt kort'}
                        </>

                    ) :
                        (
                            <>
                                Klart
                            </>
                        )}

                    <Button
                        type="button"
                        className="button-close Button white-bg"
                        aria-label="Stäng dialog"
                        onClick={handleClose}
                    >
                        <CloseIcon />
                    </Button>
                </h2>

                <div className={classNames(
                    "order-new-card-dialog__description",
                    { 'order-replacement-description': orderReplacement })}>
                    {!success ? (
                        <p>
                            {orderReplacement
                                ? '' //This is for replacement cards no description needed
                                : 'Här kan du beställa ett nytt kort. Vänligen välj vilken typ av kort du önskar beställa nedan.'
                            }
                        </p>
                    ) : (
                        <div className="order-complete">
                            <p>
                                {orderReplacement
                                    ? `Ditt ersättningskort är beställt. Ditt gamla kort ${disableNow ? 'har spärras omgående.' : 'kommer att spärras om 10 dagar.'}`
                                    : 'Ditt nya kort är beställt och kommer att skickas inom några arbetsdagar. Du kommer att kunna se ditt kort i listan över kort när det har aktiverats.'
                                }
                            </p>
                            <button className="Button body-text" onClick={() => {
                                setSuccess(false);
                                onClose();
                                onSuccess?.();
                            }}>
                                Stäng
                            </button>
                        </div>
                    )}

                    {!orderReplacement && (!hasP || !hasK) && (
                        <p className="qstar-cards-info">
                            Läs mer om våra
                            <Link ariaLabel={'Läs mer om Qstars kort'} href={cardUrl} openNewTab={true}>
                                Qstarkort
                            </Link>
                        </p>
                    )}
                </div>

                {orderReplacement && (
                    <div className={classNames(
                        "order-new-card-dialog__card-info",
                        { 'hide': success })}>
                        <div className="card-info-item">
                            <span className="card-info-item__label">Kortnamn:</span>
                            <span className="card-info-item__value">{cardholderName}</span>
                        </div>

                        <div className="card-info-item">
                            <span className="card-info-item__label">Korttyp:</span>
                            <span className="card-info-item__value">{cardType}</span>
                        </div>

                        <div className="card-info-item">
                            <span className="card-info-item__label">Kortnummer:</span>
                            <span className="card-info-item__value">{card?.maskedPan}</span>
                        </div>
                    </div>
                )}

            </div>
            <div className={classNames(
                "order-new-card-dialog__form-wrapper",
                { 'hide': success }
            )}>
                {!orderReplacement && !success ? (
                    <>
                        <form id="order-new-card-form" onSubmit={handleSubmit}>
                            <div className="radio-buttons-wrapper">
                                <span>
                                    Välj korttyp<span className="red-text">*</span>
                                    <Tooltip position='top' text={cardTypeTooltipText}>
                                        <InfoIconOutlined width={20} height={20} />
                                    </Tooltip>
                                </span>

                                <div className="radio-buttons">
                                    <label
                                        className={classNames({ 'disabled': !hasK })}
                                        tabIndex={hasK ? 0 : -1}
                                        onKeyDown={(e) => { if (hasK && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setSelectedCardType(isCompany ? 'Företagskort' : 'Tankkort'); } }}
                                    >
                                        <input
                                            type="radio"
                                            name="cardType"
                                            value={isCompany ? 'Företagskort' : 'Tankkort'}
                                            checked={selectedCardType === (isCompany ? 'Företagskort' : 'Tankkort')}
                                            onChange={(e) => setSelectedCardType(e.target.value)}
                                            tabIndex={-1}
                                            disabled={!hasK}
                                        />
                                        {isCompany ? 'Företagskort' : 'Tankkort'}
                                    </label>

                                    <label
                                        tabIndex={0}
                                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedCardType('Smartkort'); } }}
                                    >
                                        <input
                                            type="radio"
                                            name="cardType"
                                            value="Smartkort"
                                            checked={selectedCardType === 'Smartkort'}
                                            onChange={(e) => setSelectedCardType(e.target.value)}
                                            tabIndex={-1}
                                        />
                                        Smartkort
                                    </label>
                                </div>
                            </div>

                            <div className="card-reference">
                                <span>
                                    Namn på kortet
                                    <Tooltip position='top' text="Här kan du ange ett kortnamn för att lätt särskilja korten på fakturan.">
                                        <InfoIconOutlined width={20} height={20} />
                                    </Tooltip>
                                </span>

                                <input
                                    type="text"
                                    name="cardholderName"
                                    placeholder="Namn eller kortreferens"
                                    value={cardholderName}
                                    onChange={handleCardholderNameChange}
                                    maxLength={26}
                                />
                            </div>

                            <div className="card-pin-wrapper">
                                <div className="pin-item">
                                    <span>
                                        Välj PIN-kod<span className="red-text">*</span>
                                        <Tooltip position='top' text="Här väljer du en pinkod för ditt kort. Pinkoden måste vara 4 siffror.">
                                            <InfoIconOutlined width={20} height={20} />
                                        </Tooltip>
                                    </span>
                                    <input
                                        type="text"
                                        name="cardPin"
                                        placeholder="Välj PIN-kod"
                                        value={pin}
                                        onChange={handlePinChange}
                                        maxLength={4}
                                        inputMode="numeric"
                                        required
                                    />
                                    {pinError && (
                                        <span className="input-error">{pinError}</span>
                                    )}
                                </div>
                                <div className="pin-item">
                                    <span>
                                        Bekräfta PIN-kod<span className="red-text">*</span>
                                    </span>
                                    <input
                                        ref={pinConfirmRef}
                                        type="text"
                                        name="cardPinConfirm"
                                        placeholder="Bekräfta PIN-kod"
                                        value={pinConfirm}
                                        onChange={handlePinConfirmChange}
                                        maxLength={4}
                                        inputMode="numeric"
                                        required
                                    />
                                    {pinConfirmError && (
                                        <span className="input-error">{pinConfirmError}</span>
                                    )}
                                </div>

                            </div>

                            {creditCheckStatusMessage && (
                                <p className={creditCheckEvaluationError ? 'input-error' : undefined}>
                                    {creditCheckStatusMessage}
                                </p>
                            )}
                            {needNewCreditCheck && selectedCardType !== 'Smartkort' && (
                                <div className="card-need-credit-check">
                                    <label >
                                        <input type="checkbox" id="needCreditCheck" name="needCreditCheck" checked={creditCheckboxChecked} onChange={(e) => setCreditCheckboxChecked(e.target.checked)} />
                                        Jag godkänner att en kreditprövning kommer att göras på mig i samband med denna beställning
                                        <Tooltip position='top-end' text="Det var mer än 6 månader sedan en kreditupplysning gjordes, därför kommer en ny att göras.">
                                            <InfoIconOutlined width={20} height={20} />
                                        </Tooltip>
                                    </label>
                                </div>
                            )}


                            {(submitError || mutationError) && (
                                <p className="input-error">
                                    {submitError || 'Något gick fel. Försök igen eller kontakta kundservice.'}
                                </p>
                            )}
                            <button className="Button body-text" aria-disabled={!isFormValid || mutationLoading || creditCheckSubmissionBlocked} type="submit" disabled={!isFormValid || mutationLoading || creditCheckSubmissionBlocked}>
                                {mutationLoading ? 'Beställer...' : submitButtonText}
                            </button>
                        </form>
                    </>
                ) : !success && (
                    <>
                        {/* Replacement card form */}
                        <form id="order-replacement-card-form" onSubmit={handleSubmit}>
                            <div className="card-reference">
                                <span>
                                    Namn på kortet
                                    <Tooltip position='top' text="Önskar du byta kortnamn kan du göra det här.">
                                        <InfoIconOutlined width={20} height={20} />
                                    </Tooltip>
                                </span>

                                <input
                                    type="text"
                                    name="cardholderNameReplacement"
                                    placeholder="Namn eller kortreferens"
                                    value={cardholderName}
                                    onChange={handleCardholderNameChange}
                                    maxLength={25}
                                />
                            </div>

                            <div className="card-replacement-settings">
                                <label >
                                    <input type="checkbox" id="keepPin" name="keepPin" checked={keepPin} onChange={(e) => setKeepPin(e.target.checked)} />
                                    Behåll nuvarande PIN-kod
                                </label>

                                {!keepPin && (
                                    <div className="card-pin-wrapper">
                                        <div className="pin-item">
                                            <span>
                                                Välj PIN-kod<span className="red-text">*</span>
                                                <Tooltip position='top' text="Välj en PIN-kod för ditt kort. Denna kod kommer du använda när du tankar eller gör inköp med kortet. Pinkoden måste vara 4 siffror lång.">
                                                    <InfoIconOutlined width={20} height={20} />
                                                </Tooltip>
                                            </span>
                                            <input
                                                type="text"
                                                name="cardPin"
                                                placeholder="Välj PIN-kod"
                                                value={pin}
                                                onChange={handlePinChange}
                                                maxLength={4}
                                                inputMode="numeric"
                                                required
                                            />
                                            {pinError && (
                                                <span className="input-error">{pinError}</span>
                                            )}
                                        </div>
                                        <div className="pin-item">
                                            <span>
                                                Bekräfta PIN-kod<span className="red-text">*</span>
                                            </span>
                                            <input
                                                ref={pinConfirmRef}
                                                type="text"
                                                name="cardPinConfirm"
                                                placeholder="Bekräfta PIN-kod"
                                                value={pinConfirm}
                                                onChange={handlePinConfirmChange}
                                                maxLength={4}
                                                inputMode="numeric"
                                                required
                                            />
                                            {pinConfirmError && (
                                                <span className="input-error">{pinConfirmError}</span>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="radio-buttons-wrapper">
                                <span>
                                    Spärra nuvarande kort
                                    <Tooltip position='top' text="Välj om du vill spärra ditt nuvarande kort direkt eller om 10 dagar från och med idag.">
                                        <InfoIconOutlined width={20} height={20} />
                                    </Tooltip>
                                </span>

                                <div className="radio-buttons">
                                    <label
                                        className={classNames('radio-buttons-label')}
                                        tabIndex={0}
                                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDisableNow(true); } }}
                                    >
                                        <input
                                            type="radio"
                                            name="disableCard"
                                            value="Spärra direkt"
                                            checked={disableNow}
                                            onChange={(e) => setDisableNow(true)}
                                            tabIndex={-1}
                                        />
                                        Spärra direkt
                                    </label>

                                    <label
                                        className={classNames('radio-buttons-label')}
                                        tabIndex={0}
                                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDisableNow(false); } }}
                                    >
                                        <input
                                            type="radio"
                                            name="disableCard"
                                            value="Spärra om 10 dagar"
                                            checked={!disableNow}
                                            onChange={(e) => setDisableNow(false)}
                                            tabIndex={-1}
                                        />
                                        Spärra om 10 dagar
                                    </label>
                                </div>
                            </div>

                            {creditCheckStatusMessage && (
                                <p className={creditCheckEvaluationError ? 'input-error' : undefined}>
                                    {creditCheckStatusMessage}
                                </p>
                            )}
                            {needNewCreditCheck && card?.type.shortName === 'K' && (
                                <div className="card-need-credit-check">
                                    <label>
                                        <input type="checkbox" id="needCreditCheck" name="needCreditCheck" checked={creditCheckboxChecked} onChange={(e) => setCreditCheckboxChecked(e.target.checked)} />
                                        Jag godkänner att en kreditprövning kommer att göras på mig i samband med denna beställning
                                        <Tooltip position='top-end' text="Det var mer än 6 månader sedan en kreditupplysning gjordes, därför kommer en ny att göras.">
                                            <InfoIconOutlined width={20} height={20} />
                                        </Tooltip>
                                    </label>
                                </div>
                            )}

                            <button className="Button body-text" aria-disabled={!isReplacementFormValid || mutationLoading || creditCheckSubmissionBlocked} type="submit" disabled={!isReplacementFormValid || mutationLoading || creditCheckSubmissionBlocked}>
                                {mutationLoading ? 'Beställer...' : submitButtonText}
                            </button>
                        </form>
                    </>
                )}

            </div>
        </dialog>
    );
}

export default OrderNewCard;